import { useEffect, useMemo, useRef, useState } from "react";
import { notifyCatalogUpdated } from "../../core/catalogSync";

const pickByPath = (obj, path) => {
  if (!path) return obj;
  return String(path)
    .split(".")
    .reduce((acc, key) => (acc == null ? undefined : acc[key]), obj);
};

/** ZIP import returns /uploaded-products/...; CRA dev often 404s those (public/ vs live API). */
const previewSrcForAdmin = (src) => {
  const s = String(src || "").trim();
  if (!s) return s;
  if (/^https?:\/\//i.test(s)) return s;
  if (!s.startsWith("/uploaded-products/")) return s;
  if (typeof window === "undefined") return s;
  const host = window.location.hostname;
  const port = String(window.location.port || "");
  const isLocal = host === "localhost" || host === "127.0.0.1";
  if (!isLocal || port !== "3000") return s;
  const apiPort = process.env.REACT_APP_UPLOAD_API_PORT || "4001";
  return `http://127.0.0.1:${apiPort}${s}`;
};

export default function UploadApiField({
  value,
  onChange,
  multiple = false,
  maxFiles = 1,
  maxFileSizeMB = 10,
  accept = ".zip,application/zip",
  uploadApi = {},
  uploadStructure = {},
  uploadContext = {},
  autoUpload = true,
  /** Bumps when admin form session changes (Add Product, row select, reset) — clears local file UI. */
  widgetResetToken,
}) {
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [uploadedPreview, setUploadedPreview] = useState([]);
  const fileInputRef = useRef(null);
  const activeSku = String(uploadContext?.sku || "").trim();
  const prevSkuRef = useRef(activeSku);
  const keepPreviewOnNextSkuChangeRef = useRef(false);

  useEffect(() => {
    if (widgetResetToken === undefined || widgetResetToken === null) return;
    setFiles([]);
    setError("");
    setInfo("");
    setUploadedPreview([]);
    keepPreviewOnNextSkuChangeRef.current = false;
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, [widgetResetToken]);

  useEffect(() => {
    if (prevSkuRef.current !== activeSku) {
      prevSkuRef.current = activeSku;
      if (keepPreviewOnNextSkuChangeRef.current) {
        // SKU may be auto-populated from the same ZIP upload response.
        keepPreviewOnNextSkuChangeRef.current = false;
        return;
      }
      setFiles([]);
      setError("");
      setInfo("");
      setUploadedPreview([]);
      return;
    }
    if (!activeSku && (!value || (typeof value === "string" && !String(value).trim()))) {
      setFiles([]);
      setError("");
      setInfo("");
      setUploadedPreview([]);
    }
  }, [activeSku, value]);

  const maxBytes = useMemo(() => {
    const mb = Number(maxFileSizeMB);
    return Number.isFinite(mb) && mb > 0 ? mb * 1024 * 1024 : null;
  }, [maxFileSizeMB]);

  const endpoint = uploadApi?.url || "";
  const isLocalHostRuntime =
    typeof window !== "undefined" &&
    (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");
  const resolvedEndpoint =
    typeof window !== "undefined" &&
    typeof endpoint === "string" &&
    endpoint.startsWith("http://localhost:") &&
    window.location.hostname &&
    !isLocalHostRuntime
      ? endpoint.replace("localhost", window.location.hostname)
      : endpoint;
  const sameOriginApiEndpoint =
    resolvedEndpoint && resolvedEndpoint.startsWith("/api/") && typeof window !== "undefined"
      ? `${window.location.origin}${resolvedEndpoint}`
      : "";
  const localhostEndpoint =
    resolvedEndpoint && resolvedEndpoint.startsWith("/api/") && isLocalHostRuntime
      ? `http://localhost:4001${resolvedEndpoint}`
      : "";
  const debug = uploadApi?.debug === true;
  const dbg = (...args) => {
    if (debug) {
      // eslint-disable-next-line no-console
      console.log("[UploadApiField]", ...args);
    }
  };

  const validateFiles = (picked) => {
    const list = Array.from(picked || []);
    if (!list.length) return [];
    let filtered = list;
    if (maxBytes) {
      filtered = filtered.filter((f) => f.size <= maxBytes);
      if (filtered.length !== list.length) setError(`Some files skipped. Max ${maxFileSizeMB} MB per file.`);
    }
    const cap = Number(maxFiles) > 0 ? Number(maxFiles) : filtered.length;
    return filtered.slice(0, cap);
  };

  const uploadNow = async (chosenFiles) => {
    dbg("uploadNow:start", {
      endpoint,
      resolvedEndpoint,
      sameOriginApiEndpoint,
      localhostEndpoint,
      fileCount: chosenFiles?.length || 0,
      files: (chosenFiles || []).map((f) => ({ name: f.name, size: f.size, type: f.type })),
      uploadContext,
      uploadStructure,
    });
    if (!endpoint) {
      setError("Upload API URL is missing in JSON config.");
      dbg("uploadNow:error", "Missing endpoint");
      return;
    }
    if (!chosenFiles.length) {
      setError("Select at least one file.");
      dbg("uploadNow:error", "No files selected");
      return;
    }
    setBusy(true);
    setError("");
    setInfo("");
    try {
      const formData = new FormData();
      const fieldName = uploadApi.fileField || "files";
      chosenFiles.forEach((f) => formData.append(fieldName, f, f.name));
      formData.append("structure", JSON.stringify(uploadStructure || {}));
      formData.append("context", JSON.stringify(uploadContext || {}));
      if (uploadApi.staticFields && typeof uploadApi.staticFields === "object") {
        Object.entries(uploadApi.staticFields).forEach(([k, v]) => {
          formData.append(k, typeof v === "string" ? v : JSON.stringify(v));
        });
      }

      const candidates = [
        resolvedEndpoint,
        sameOriginApiEndpoint,
        localhostEndpoint,
      ].filter((v, i, arr) => v && arr.indexOf(v) === i);
      let res = null;
      let body = {};
      let lastErr = null;
      for (const candidate of candidates) {
        try {
          dbg("uploadNow:tryEndpoint", candidate);
          const trialRes = await fetch(candidate, {
            method: uploadApi.method || "POST",
            body: formData,
          });
          const trialBody = await trialRes.json().catch(() => ({}));
          dbg("uploadNow:endpointResponse", { candidate, status: trialRes.status, ok: trialRes.ok, body: trialBody });
          res = trialRes;
          body = trialBody;
          if (trialRes.ok) break;
        } catch (err) {
          lastErr = err;
          dbg("uploadNow:endpointException", { candidate, message: err?.message });
        }
      }
      if (!res && lastErr) throw lastErr;
      if (!res.ok) {
        dbg("uploadNow:errorResponse", { status: res.status, body });
        throw new Error(body?.message || `Upload failed (${res.status})`);
      }
      const payload = pickByPath(body, uploadApi.responseDataPath) || body;
      dbg("uploadNow:successPayload", payload);
      let previewUrls = Array.isArray(payload?.images)
        ? payload.images.map((u) => String(u || "").trim()).filter(Boolean)
        : typeof payload?.images === "string" && payload.images.trim()
          ? payload.images.split(",").map((v) => v.trim()).filter(Boolean)
          : [];
      if (!previewUrls.length && Array.isArray(payload?.savedFiles)) {
        previewUrls = payload.savedFiles.map((u) => String(u || "").trim()).filter(Boolean);
      }
      setUploadedPreview(previewUrls.slice(0, 12));
      if (payload && typeof payload === "object") {
        keepPreviewOnNextSkuChangeRef.current = true;
        onChange({ __structuredImport: true, ...payload });
      } else {
        keepPreviewOnNextSkuChangeRef.current = false;
        onChange(payload);
      }
      setInfo(uploadApi.successMessage || "Upload completed.");
      notifyCatalogUpdated();
    } catch (e) {
      const msg = e?.message || "Upload failed";
      if (/Failed to fetch/i.test(msg)) {
        const hint = sameOriginApiEndpoint || localhostEndpoint || endpoint;
        setError(`Network error: cannot reach upload API. Ensure API is running at ${hint}.`);
      } else {
        setError(msg);
      }
      dbg("uploadNow:exception", e);
    } finally {
      setBusy(false);
      dbg("uploadNow:end");
    }
  };

  return (
    <div>
      <input
        ref={fileInputRef}
        type="file"
        multiple={multiple}
        accept={accept}
        onChange={(e) => {
          const valid = validateFiles(e.target.files);
          setFiles(valid);
          if (autoUpload) uploadNow(valid);
        }}
      />
      <div style={{ marginTop: 6, fontSize: 12, color: "#7a5e45" }}>
        API: {endpoint || "Not configured"} | Max {maxFileSizeMB} MB | Files: {maxFiles}
      </div>
      {!autoUpload ? (
        <button
          type="button"
          className="lfx-btn secondary sm"
          onClick={() => uploadNow(files)}
          disabled={busy || !files.length}
          style={{ marginTop: 8 }}
        >
          {busy ? "Uploading..." : uploadApi.buttonLabel || "Upload to server"}
        </button>
      ) : null}
      {error ? <div style={{ marginTop: 6, fontSize: 12, color: "#c0392b" }}>{error}</div> : null}
      {info ? <div style={{ marginTop: 6, fontSize: 12, color: "#2d7a3a" }}>{info}</div> : null}
      {value && typeof value === "string" ? (
        <div style={{ marginTop: 6, fontSize: 12, color: "#5c4a38" }}>{value}</div>
      ) : null}
      {uploadedPreview.length ? (
        <div
          className="lfx-upload-zip-previews"
          style={{ marginTop: 10, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-start" }}
        >
          {uploadedPreview.map((src, i) => {
            const displaySrc = previewSrcForAdmin(src);
            const thumb = 176;
            return (
              <img
                key={`${displaySrc}-${i}`}
                src={displaySrc}
                alt=""
                width={thumb}
                height={thumb}
                loading="lazy"
                style={{
                  objectFit: "contain",
                  objectPosition: "top center",
                  borderRadius: 8,
                  border: "1px solid #e8e0d5",
                  background: "#fff",
                  padding: 4,
                  display: "block",
                  boxSizing: "border-box",
                }}
              />
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
